import { AppThunk } from "@Store/index";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import getBookFormPageData from "./getBookFormPageData";
import getCookie from "helpers/getCookie";
import getCurrencySymbol from "helpers/getCurrencySymbol";

export interface BookFromData {
  readyStatus: "invalid" | "request" | "success" | "failure";
  canonical_name: string;
  data: any;
  error: any;
  isLoading: boolean;
  paymentActive: boolean;
  formData: Record<any, any>;
  isCustomFormEnabled: boolean;
  isCustomFormAfterPaymentEnabled: boolean;
  skipGuarantorDetails: boolean;
  inventoryForm: Record<any, any>;
  formSectionObj: Record<any, any>;
  formSectionObjAfterPayment: Record<any, any>;
  initialFromState: Record<any, any>;
  inventoryCardSummary: any;
  paymentState: string;
  paymentIntentObject: any;
  priceSummary: any;
  leadData: any;
  isPaymentFormWithAccommodationCard: boolean;
  paymentData: any;
  paymentTokenData: {
    token: "";
    data: any;
    err: any;
  };
  isPaymentDataSaved: boolean;
  paymentInfo: any;
  isPaymentDataSynced: boolean;
  isTokenBasedPayment: boolean;
  isGlobalFormFieldError: {
    isAccommodationFieldError: boolean;
    isEmergencyFieldError: boolean;
    isContactDetailFieldError: boolean;
  };
  isErrorForAccommodationCard: boolean;
  formAssets: Record<any, any>[];
  paymentSummaryForBookForm: any;
}

export const initialState: BookFromData = {
  data: {},
  error: null,
  formData: {},
  isLoading: false,
  inventoryForm: {},
  formSectionObj: {},
  formSectionObjAfterPayment: {},
  canonical_name: "",
  paymentActive: false,
  initialFromState: {},
  readyStatus: "invalid",
  inventoryCardSummary: {},
  isCustomFormEnabled: false,
  isCustomFormAfterPaymentEnabled: false,
  skipGuarantorDetails: false,
  paymentState: "loading",
  paymentIntentObject: {},
  priceSummary: {},
  leadData: {},
  isPaymentFormWithAccommodationCard: false,
  paymentData: {},
  paymentTokenData: {
    token: "",
    data: {},
    err: null,
  },
  isPaymentDataSaved: false,
  isPaymentDataSynced: false,
  paymentInfo: {},
  isTokenBasedPayment: false,
  isGlobalFormFieldError: {
    isAccommodationFieldError: false,
    isEmergencyFieldError: false,
    isContactDetailFieldError: false,
  },
  isErrorForAccommodationCard: false,
  formAssets: [],
  paymentSummaryForBookForm: {},
};

const bookFromData = createSlice({
  name: "bookFromData",
  initialState,
  reducers: {
    updateIsPaymentFormWithAccommodationCard: (state: BookFromData, { payload }) => {
      const queryParams = new URLSearchParams(window.location.search);
      const regex = /_pre_filled|_no_pre_filled_(copy|open)(_with_data|_without_data)?$/;
      const isAgentGeneratedPaymentLink =
        queryParams.get("utm_medium") === "agent" ||
        queryParams?.get("utm_medium") === "system" ||
        regex.test(queryParams?.get("payment_source") || "");
      const currentRoom = payload?.accommodationDetails?.roomType
        ? state.data.active_children?.find(
            (room: any) => room.id == payload?.accommodationDetails?.roomType,
          )
        : null;
      const isPersonalDetails = Boolean(
        payload?.personalDetails?.["user_details/email"] &&
          payload?.personalDetails?.["user_details/phone"],
      );
      state.isPaymentFormWithAccommodationCard =
        isAgentGeneratedPaymentLink ||
        !currentRoom ||
        !isPersonalDetails ||
        payload?.isPaymentBeforeBookform ||
        !getCookie("user_flow_token");
    },
    getBookFormPageRequesting: (state: BookFromData, { payload }: PayloadAction<any>) => {
      // Reset payment-related state to prevent stale data from a previous inventory
      // showing on the new booking form. Skip reset on success page only if the
      // property hasn't changed (i.e. payment summary belongs to this inventory).
      const isSameProperty = state.canonical_name === payload.id;
      if (!(payload.isSuccessPage && isSameProperty)) {
        state.paymentInfo = {};
        state.paymentSummaryForBookForm = {};
        state.priceSummary = {};
        state.paymentData = {};
        state.paymentState = "loading";
        state.paymentIntentObject = {};
        state.isPaymentDataSaved = false;
        state.isPaymentDataSynced = false;
      }
      state.canonical_name = payload.id;
      state.readyStatus = "request";
      if (__CLIENT__) window.scrollTo(0, 0);
    },
    updatePaymentPageDataLoaded: (state: BookFromData, { payload }: PayloadAction<any>) => {
      state.paymentState = payload;
    },
    getBookFormPageSuccess: (state: BookFromData, { payload }: PayloadAction<any>) => {
      state.data = payload?.data;
      state.inventoryForm = payload?.inventoryForm;
      state.isCustomFormEnabled = payload?.isCustomFormEnabled;
      state.isCustomFormAfterPaymentEnabled = payload?.isCustomFormAfterPaymentEnabled;
      state.formSectionObj = payload?.formSectionObj;
      state.formSectionObjAfterPayment = payload?.formSectionObjAfterPayment;
      state.skipGuarantorDetails = payload?.skipGuarantorDetails;
      state.paymentActive = payload?.paymentActive;
      state.readyStatus = "success";
    },

    getBookFormPageError: (state: BookFromData, { payload }: PayloadAction<any>) => {
      state.error = payload;
      state.readyStatus = "failure";
    },

    updateInventoryCardSummary: (state: BookFromData, { payload }: PayloadAction<any>) => {
      state.inventoryCardSummary = payload.cardSummary;
    },

    initFormData: (state: BookFromData, { payload }: PayloadAction<any>) => {
      state.formData = {
        ...(state.formData || {}),
        ...payload,
      };
    },

    updatePaymentdata: (state: BookFromData, { payload }: PayloadAction<any>) => {
      state.paymentData = payload;
      state.isPaymentDataSynced = true;
    },

    updateInitialFormState: (state: BookFromData, { payload }: PayloadAction<any>) => {
      state.initialFromState = payload.initialFromState;
    },

    toggleLoading: (state: BookFromData, { payload }: PayloadAction<any>) => {
      state.isLoading = payload.isLoading;
    },

    togglePaymentSyncDataState: (state: BookFromData, { payload }: PayloadAction<any>) => {
      state.isPaymentDataSynced = payload.isPaymentDataSynced;
    },

    updatePaymentState: (state: BookFromData, { payload }: PayloadAction<any>) => {
      state.paymentState = payload;
    },

    updatePaymentIntent: (state: BookFromData, { payload }: PayloadAction<any>) => {
      state.paymentIntentObject = payload;
    },

    updatePriceSummary: (state: BookFromData, { payload }: PayloadAction<any>) => {
      const totalAmountFromInfo = payload?.payment_info?.find((info: any) => info.type === "total");
      state.priceSummary = payload;
      if (totalAmountFromInfo && "original_currency" in totalAmountFromInfo) {
        state.priceSummary = {
          ...state.priceSummary,
          currencyConverted: getCurrencySymbol(totalAmountFromInfo.currency),
          totalAmount: totalAmountFromInfo.amount,
          currency: getCurrencySymbol(totalAmountFromInfo?.original_currency),
          totalAmountConverted: totalAmountFromInfo.amount_converted,
        };
      }
    },

    updateLeadData: (state: BookFromData, { payload }: PayloadAction<any>) => {
      state.leadData = payload;
    },

    updateTokenPaymentData: (state: BookFromData, { payload }: PayloadAction<any>) => {
      state.isTokenBasedPayment = true;
      state.paymentTokenData.token = payload.token;
      state.paymentTokenData.data = payload.data;
      state.paymentTokenData.err = payload.err;
    },
    updatePaymentDataSavedFlag: (state: BookFromData, { payload }: PayloadAction<any>) => {
      state.isPaymentDataSaved = payload;
    },

    updateNationalityData: (state: BookFromData, { payload }: PayloadAction<any>) => {
      state.data = {
        ...state.data,
        custom_nationality_payment: payload?.custom_nationality_payment,
      };
    },
    updatePaymentInfo: (state: BookFromData, { payload }: PayloadAction<any>) => {
      state.paymentInfo = payload;
    },
    updatePaymentSummaryForBookForm: (state: BookFromData, { payload }: PayloadAction<any>) => {
      state.paymentSummaryForBookForm = payload;
    },
    toggleIsGlobalFormFieldError: (state: BookFromData, { payload }: PayloadAction<any>) => {
      state.isGlobalFormFieldError = {
        ...state.isGlobalFormFieldError,
        ...payload,
      };
    },
    updateFormAssets: (state: BookFromData, { payload }: PayloadAction<any>) => {
      state.formAssets = payload.formAssets;
    },
  },
});

export const shouldFetchBookForm = (bookFromDataState: BookFromData, canonical_name: string) =>
  bookFromDataState.readyStatus === "invalid" ||
  bookFromDataState.canonical_name !== canonical_name;

export const fetchBookFormPageDataIfNeeded =
  (props: any, canonicalName = "", isSuccessPage = false): AppThunk =>
  (dispatch, getState: any) => {
    const canonical_name =
      canonicalName || __CLIENT__ ? props?.match?.params?.id : props?.params?.id;
    const href = (__CLIENT__ ? window.location.href : props.req?.url) || null;
    let token = "";

    if (!__CLIENT__) {
      token = props.req.cookies.user_auth_token;
      dispatch(updateInitialFormState({ initialFromState: props.req.query }));
    }

    const state = getState();
    if (shouldFetchBookForm(getState().bookFormData, canonical_name)) {
      return dispatch(
        getBookFormPageData({
          id: canonicalName || (props.canonical_name ?? canonical_name),
          url: props.href ?? href,
          staticContext: props.staticContext,
          token,
          req: props?.req,
          isSuccessPage,
        }),
      );
    }

    if (state.router.action === "PUSH" && __CLIENT__) {
      window.scrollTo(0, 0);
    }

    return null;
  };

export default bookFromData.reducer;

export const {
  toggleLoading,
  getBookFormPageError,
  getBookFormPageSuccess,
  updateInitialFormState,
  getBookFormPageRequesting,
  updateInventoryCardSummary,
  updatePaymentState,
  updatePaymentIntent,
  updatePriceSummary,
  updateLeadData,
  initFormData,
  updateIsPaymentFormWithAccommodationCard,
  updatePaymentdata,
  updatePaymentPageDataLoaded,
  updateTokenPaymentData,
  updatePaymentDataSavedFlag,
  updateNationalityData,
  togglePaymentSyncDataState,
  updatePaymentInfo,
  toggleIsGlobalFormFieldError,
  updateFormAssets,
  updatePaymentSummaryForBookForm,
} = bookFromData.actions;
